import { Injectable, inject } from '@angular/core';
import { ObjectState } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { APP_CONSTANTS, IModuleModel, ModuleSettingsRealm, ModuleType, SharedAppState } from '@shared/core';
import { ModuleMapper } from '@shared/data';
import { Observable, forkJoin, map, switchMap, tap } from 'rxjs';
import { CssFetcherService } from '../../infrastructure/service-client/css-fetcher.service';
import { McaAppActions } from '../../infrastructure/store/mca-app/mca-app.actions';

@Injectable({
    providedIn: 'root',
})
export class AppStyleManagerService {
    private readonly cssFetcher = inject(CssFetcherService);
    private readonly store = inject(Store);
    private readonly appConstants = inject(APP_CONSTANTS);
    public loadAppStyle(): Observable<void> {
        return this.store.selectOnce(SharedAppState.appModules$).pipe(
            switchMap(appModules => {
                const realmModule = new ModuleMapper().mapModulesToTypedModule<ModuleSettingsRealm>(ModuleType.REALM, appModules as IModuleModel[]);
                return this.cssFetcher.fetchCss(realmModule.settings.clientId);
            }),
            switchMap(cssUrl => {
                return forkJoin([
                    this.store.dispatch(new McaAppActions.SetStyle({ state: ObjectState.VALID, value: cssUrl })),
                    this.waitForElm<HTMLLinkElement>(this.appConstants.styleLoader.styleSheetId).pipe(map(el => ({ element: el, url: cssUrl }))),
                ]);
            }),
            tap(([, d]) => {
                d.element.href = d.url;
            }),
            map(() => void 0),
        );
    }

    private waitForElm<T = Element>(selector: string): Observable<T> {
        return new Observable<T>(observer => {
            const el = document.querySelector(`#${selector}`);
            if (el) {
                observer.next(el as T);
                observer.complete();
                return;
            }

            const mutationObserver = new MutationObserver(() => {
                const el = document.querySelector(`#${selector}`);
                if (el) {
                    mutationObserver.disconnect();
                    observer.next(el as T);
                    observer.complete();
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        });
    }
}
